import { atom } from 'recoil';

import * as K from 'common/constants';
import { AvailableTheme, LogoInfo, NavbarVariant, Settings } from 'common/types';
import { DEFAULT_THEME } from 'service/browser';
import { accountDataState } from 'components/account/account.state';
import { searchParamState } from 'components/common/Managers/RouteStateManager.state';
import { adjustTheme, THEME_INFO } from 'styles/stylesProvider/themes';
import { recoilSelector } from 'common/util/recoil-selector';
import logoDataSetLight from 'assets/img/logo-dataset-light.svg';
import logoDataSetDark from 'assets/img/logo-dataset-dark.svg';
import logoXDRDark from 'assets/img/logo-xdr-dark.svg';
import { settingsObj } from 'api/apolloClient';

/**
 * User settings in memory {@see Settings}
 */
export const settingsState = atom<Settings>({
  key: K.GLOBAL_KEY.SETTINGS,
  default: K.DEFAULT_SETTINGS,
  effects: [
    ({ onSet }) => {
      onSet((value) => {
        settingsObj.publish(value);
      });
    },
  ],
});

/**
 * User settings persisted on the server {@see Settings}
 */
export const savedSettingsState = atom<Settings>({
  key: K.GLOBAL_KEY.SAVED_SETTINGS,
  default: K.DEFAULT_SETTINGS,
});

/**
 * False until settings have been loaded.
 */
export const settingsProcessedState = atom({
  key: K.GLOBAL_KEY.SETTINGS_PROCESSED,
  default: false,
});

/**
 * Theme can come from URL (which takes precedence), or settings. If it comes from settings,
 * it can be adjusted to be a SentinelOne theme.
 */
export const themeState = recoilSelector<AvailableTheme>({
  key: K.RECOIL_SELECTOR_KEY.THEME,
  get: ({ get }) => {
    const urlTheme = get(searchParamState(K.URL_PARAM.THEME)) as unknown as AvailableTheme;
    if (urlTheme && Object.values(AvailableTheme).includes(urlTheme)) {
      return urlTheme;
    }
    const settings = get(settingsState);
    const theme = settings?.[K.SETTING.THEME];
    if (theme) {
      const account = get(accountDataState);
      const theme1 = adjustTheme(theme, account.isSingularityXDRUser ?? false);
      return theme1;
    }
    return DEFAULT_THEME;
  },
});

// logo to use on our navbars, based on theme
export const logoState = recoilSelector<LogoInfo>({
  key: K.RECOIL_SELECTOR_KEY.LOGO,
  get: ({ get }) => {
    const account = get(accountDataState);
    const theme = get(themeState);
    const isLightTheme = !THEME_INFO[theme]?.isDark;

    if (account.isSingularityXDRUser) {
      return {
        logoUrl: logoXDRDark,
        logoWidth: '150px',
        favIcon: 'favicon-s1.ico',
      };
    }

    return {
      logoUrl: isLightTheme ? logoDataSetLight : logoDataSetDark,
      logoWidth: '100px',
      favIcon: 'favicon.png',
    };
  },
});

// Bootstrap variant to use for our navbars, based on theme
export const navbarVariantState = recoilSelector<NavbarVariant>({
  key: K.RECOIL_SELECTOR_KEY.NAVBAR_VARIANT,
  get: ({ get }) => {
    const theme = get(themeState);
    let navbarVariant = NavbarVariant.DARK;
    if (theme === AvailableTheme.SENTINEL_ONE) {
      navbarVariant = NavbarVariant.LIGHT;
    }
    return navbarVariant;
  },
});

export const displayModeSelector = recoilSelector<K.DISPLAY_MODE>({
  key: K.RECOIL_SELECTOR_KEY.DISPLAY_MODE,
  get: ({ get }) => get(settingsState)?.[K.SETTING.DISPLAY_MODE] ?? K.DEFAULT_SETTINGS[K.SETTING.DISPLAY_MODE],
});
